import { JsonRpc, RpcError } from 'eosjs'
import { Serializer } from '@greymass/eosio'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { eosChainId } from './networks'
import {
  AnchorWallet,
  ScatterWallet,
  MetamaskWallet,
  TokenPocketWallet,
  MetahubWallet,
} from './wallets'
import type {
  EosAccount,
  EosAction,
  EosConfig,
  TransactionResult,
  WalletAdapter,
  WalletType,
  TableRowsParams,
} from '@/types'

class EosTool {
  private config: EosConfig
  public rpc: JsonRpc
  private wallet: WalletAdapter | null = null
  private _account: EosAccount | null = null
  private connected: string = ''

  constructor() {
    this.config = {
      chainId: eosChainId,
      rpcUrl: 'https://eospush.mytokenpocket.vip',
    }
    this.rpc = new JsonRpc(this.config.rpcUrl)
  }

  get account(): EosAccount | null {
    return this._account
  }

  async connect(wallet: WalletType): Promise<EosAccount | null> {
    switch (wallet) {
      case 'anchor':
        this.wallet = new AnchorWallet()
        break
      case 'metamask':
        this.wallet = new MetamaskWallet()
        break
      case 'scatter':
        this.wallet = new ScatterWallet()
        break
      case 'tokenpocket':
        this.wallet = new TokenPocketWallet()
        break
      default:
        this.wallet = new MetahubWallet()
    }
    const accounts = await this.wallet.connect(this.config, this.rpc)
    this._account = accounts.length > 0 ? accounts[0] : null
    this.connected = wallet
    return this._account
  }

  updateChainId(chainId: string): void {
    this.config.chainId = chainId
  }

  updateRpc(rpcUrl: string): void {
    this.config.rpcUrl = rpcUrl
    ;(this.rpc as any).endpoint = rpcUrl
  }

  async logout(): Promise<void> {
    this._account = null
    if (this.wallet) await this.wallet.logout()
  }

  async login(wallet: WalletType): Promise<EosAccount | null> {
    if (this.connected !== wallet) await this.connect(wallet)
    if (!this._account && this.wallet) {
      const accounts = await this.wallet.login()
      if (!accounts || accounts.length === 0) {
        this._account = null
        return null
      }
      this._account = accounts[0]
    }
    return this._account
  }

  async getAccount(name: string) {
    return await this.rpc.get_account(name)
  }

  getAuthorization() {
    if (!this._account) throw new Error('Not logged in')
    return { actor: this._account.name, permission: this._account.authority }
  }

  async getCurrencyBalance(contract: string, account: string, symbol: string): Promise<string> {
    const result = await this.rpc.get_currency_balance(contract, account, symbol)
    return result.length ? result[0] : ''
  }

  async getCurrencyStats(code: string, symbol: string): Promise<{ supply: string; max_supply: string; issuer: string } | null> {
    const result = await this.rpc.get_currency_stats(code, symbol)
    return result?.[symbol] ?? null
  }

  async getTableRows<T = unknown>(params: TableRowsParams): Promise<T[]> {
    const result = await this.rpc.get_table_rows(params as any)
    return result.rows as T[]
  }

  async transact(actions: EosAction[]): Promise<TransactionResult | false> {
    if (!this._account) {
      toast.warning(i18n.t('public.noauth'))
      return false
    }
    try {
      if (!this.wallet) return false
      const result = await this.wallet.transact(actions)
      if (!result.transaction_id) {
        const res = this.handleEOSError(result)
        toast.error(res.type === 'lang' ? i18n.t(res.text) : res.text)
        return false
      }
      return result
    } catch (error) {
      const res = this.handleEOSError(error)
      toast.error(res.type === 'lang' ? i18n.t(res.text) : res.text)
      console.error(error)
      return false
    }
  }

  handleEOSError(result: unknown): { type: 'lang' | 'msg'; text: string } {
    if (typeof result === 'string') {
      try { result = JSON.parse(result) } catch { return { type: 'msg', text: result as string } }
    }
    const r = result as Record<string, unknown>
    if (r.code === 402 || r.code === 4001 || r.code === 'E_CANCEL') {
      return { type: 'lang', text: 'error.canceled' }
    }
    let error: Record<string, unknown> = {}
    if (result instanceof RpcError) {
      error = (result as any).json.error
    } else if (r.error) {
      error = r.error as Record<string, unknown>
    } else if (r.code) {
      error = r
    }
    if (error.code) {
      const code = error.code as number
      if ([3080001, 3080002, 3080004, 3040005, 3090003].includes(code)) {
        return { type: 'lang', text: `error.error${code}` }
      }
      if (error.details) {
        let msg = (error.details as Array<{ message: string }>)[0].message
        const prefix = 'assertion failure with message: '
        if (msg.startsWith(prefix)) msg = msg.substring(prefix.length)
        return { type: 'msg', text: msg }
      }
    }
    return { type: 'msg', text: String(result) }
  }

  encodeAsset(contract: string, symbol: string, precision: number): string {
    const object = {
      contract,
      quantity: '0.' + '0'.repeat(precision) + ' ' + symbol,
    }
    const value = Serializer.encode({ object, type: 'extended_asset' } as any).toString()
    return '0x' + value.substring(16) + '00000000'
  }

  decodeAsset(value: string): { symbol: string; precision: number; contract: string } {
    const data = '0000000000000000' + value.substring(2, 34)
    const object = Serializer.decode({ data, type: 'extended_asset' } as any) as any
    return {
      symbol: object.quantity.symbol.name,
      precision: object.quantity.symbol.precision,
      contract: object.contract.toString(),
    }
  }
}

export const eosTool = new EosTool()
